import CONSTANTS from './constants';

import { createConfig, extendConfig } from '@platform-shared/config';

const sessionViewer = {
  authentication: {
    localStorageToken: 'sp_token',
    localStorageRoomSid: 'sp_room',
  },
  defaultComponentState: {
    timeline: false,
    sessionDetails: false,
    sessionHeader: true,
    allowFullScreen: true,
    microphone: false,
    camera: false,
    chat: false,
    screenShare: false,
    settings: false,
    leave: false,
  },
  /*
  api: {
    url: 'https://api.sub.smarterproctoring.com',
  },
  playback: {
    url: 'https://playback.dev.proctoring.media',
  },
  */
  video: {
    validTypes: [
      CONSTANTS.VIDEO_SOURCES.LIVE,
      CONSTANTS.VIDEO_SOURCES.RECORDED,
    ], // recorded = HLS, live = RTCPeerConnection
    default: CONSTANTS.VIDEO_SOURCES.RECORDED,
    canHaveLiveVideoStatuses: ['In Progress', 'Onboarding'],
  },
};

export const API_PATHS = {
  LIST_ROOMS: '/v1/hybrid/locations/rooms',
  ROOMS: '/v1/hybrid/locations/rooms/:roomSid',
  ACTIVE_PROCTORS: '/v1/hybrid/locations/rooms/:roomSid/proctors`',
  EXIT_ROOM:
    '/v1/proctors/:proctorAccountSid/rooms/:roomSid/proctors/:proctorUserSid',
  LOGIN: '/v1/proctors/login',
  TTL: '/v1/ttl',
  EXTEND_TTL: '/v1/check',
  TOKEN_PERMISSIONS: '/v1/permissions',
  PROCTOR_ACCOUNT: '/v1/proctors/:accountSid',
  ANNOTATIONS:
    '/v1/installs/:installSid/exams/:examSid/sessions/:sessionSid/annotations',
  ROOMS_FOR_TRANSFER:
    '/v1/hybrid/installs/:installSid/exams/:examSid/sessions/:sessionSid/availableRoomsToTransfer?proctorRoomSid=:roomSid',
  TRANSFER_SESSION:
    '/v1/hybrid/installs/:installSid/exams/:examSid/sessions/:sessionSid/transferStudentToRoom?proctorRoomSid=:roomSid&requestingRoomSid=:requestingRoomSid&isInReview=:isInReview',
  GET_SESSION_DETAILS:
    '/v1/installs/:installSid/exams/:examSid/sessions/:sessionSid',
  UPDATE_SESSION_DETAILS:
    '/v1/installs/:installSid/exams/:examSid/sessions/:sessionSid',
  GET_SESSION_MEDIA_CONNECTION:
    '/v2/installs/:installSid/exams/:examSid/sessions/:sessionSid/mediaConnection',
  GET_COURSE_DETAILS: '/v1/installs/:installSid/courses/:courseSid',
  GET_USER_PROFILE: '/v1/installs/:installSid/users/:userSid/profile',
  GET_ANNOTATIONS:
    '/v2/installs/:installSid/exams/:examSid/sessions/:sessionSid/annotations',
  DELETE_ANNOTATION:
    '/v2/installs/:installSid/exams/:examSid/sessions/:sessionSid/annotations/:annotationSid',
  UPDATE_ANNOTATION:
    '/v2/installs/:installSid/exams/:examSid/sessions/:sessionSid/annotations/:annotationSid',
  ADD_ANNOTATION:
    '/v2/installs/:installSid/exams/:examSid/sessions/:sessionSid/annotations',
  GET_ANNOTATION_TYPES: '/v2/installs/:installSid/annotationTypes',
  GET_SESSION_JOURNEY:
    '/v1/installs/:installSid/exams/:examSid/sessions/:sessionSid/journey',
  REGISTER_PARTICIPANT:
    '/v1/installs/:installSid/exams/:examSid/sessions/:sessionSid/participants',
  GET_PARTICIPANTS:
    '/v1/installs/:installSid/exams/:examSid/sessions/:sessionSid/participants',
  GET_MODALITY_OPTIONS:
    '/v1/proctors/categories/:proctorTypeSubCategorySid/modalityOptions',
  ADD_ACTION:
    '/v1/installs/:installSid/exams/:examSid/sessions/:sessionSid/actions',
  GET_INCIDENT_REPORT_TYPES:
    '/v1/sessions/events/types',
  CREATE_INCIDENT_REPORT:
    '/v1/installs/:installSid/exams/:examSid/sessions/:sessionSid/events',
  GET_INCIDENT_REPORT:
    '/v1/installs/:installSid/exams/:examSid/sessions/:sessionSid/events/:eventReportSid',
  UPDATE_INCIDENT_REPORT:
    '/v1/installs/:installSid/exams/:examSid/sessions/:sessionSid/events/:eventReportSid',
  DELETE_INCIDENT_REPORT:
    '/v1/installs/:installSid/exams/:examSid/sessions/:sessionSid/events/:eventReportSid',
  CLOSE_INCIDENT_REPORT:
    '/v1/installs/:installSid/exams/:examSid/sessions/:sessionSid/events/:eventReportSid/close',
  LIST_INCIDENT_REPORTS:
    '/v1/installs/:installSid/exams/:examSid/sessions/:sessionSid/events',
  SESSION_JOURNEY_STEP:
    '/v1/installs/:installSid/exams/:examSid/sessions/:sessionSid/step',
  SESSION_END:
    '/v2/installs/:installSid/exams/:examSid/sessions/:sessionSid/end',
  GET_SESSION_PHASES:
    '/v1/installs/:installSid/exams/:examSid/sessions/:sessionSid/phases',
  MUTE_SESSION_ANNOTATIONS:
    '/v1/installs/:installSid/exams/:examSid/sessions/:sessionSid/muteAnnotations',
  IDENTITY_VALIDATION:
    '/v1/installs/:installSid/exams/:examSid/sessions/:sessionSid/identityValidation',
  GET_SYNC_DOC:
    '/v1/sessions/:sessionSid/syncdoc',
  START_REMOTE_CONTROL:
    '/v1/installs/:installSid/exams/:examSid/sessions/:sessionSid/remoteControl',
  END_REMOTE_CONTROL:
    '/v1/installs/:installSid/exams/:examSid/sessions/:sessionSid/remoteControl',
  GET_SESSION_SHARES:
    '/v2/installs/:installSid/exams/:examSid/sessions/:sessionSid/shares',
  CREATE_SESSION_SHARE:
    '/v2/installs/:installSid/exams/:examSid/sessions/:sessionSid/shares',
  UPDATE_SESSION_SHARE:
    '/v2/installs/:installSid/exams/:examSid/sessions/:sessionSid/shares/:shareSid',
  DELETE_SESSION_SHARE:
    '/v2/installs/:installSid/exams/:examSid/sessions/:sessionSid/shares/:shareSid',
  REQUEST_SESSION_SHARE_ACCESS:
    '/v2/installs/:installSid/exams/:examSid/sessions/:sessionSid/shares/access',
  GENERATE_MAGIC_LINK:
    '/v2/installs/:installSid/exams/:examSid/sessions/:sessionSid/magicLink',
};

export const MEDIA_API_PATHS = {
  GET_RECORDING_METADATA: '/v1/:token/installs/:installSid/exams/:examSid/sessions/:sessionSid/metadata',
  GET_SIGNED_SEGMENT_URL: '/v1/:token/installs/:installSid/exams/:examSid/sessions/:sessionSid/segments/sign',
};

const baseConfiguration = createConfig(import.meta.env.VITE_MODE);
const config = extendConfig('sessionViewer', sessionViewer, baseConfiguration);
if(import.meta.env.VITE_MODE !== 'production') {
  console.log(`UI running in ${import.meta.env.VITE_MODE} mode`);
}
export default sessionViewer;
export { config };